import React, {useState} from 'react'
import {useIntl} from 'react-intl'
import {summarizeUserAgent} from '../../../../utils/userAgentSummary'
import {formatDisplayIp} from '../../../../utils/formatDisplayIp'

export type ConnectionHistoryRow = {
  at?: string | Date
  ip?: string
  userAgent?: string
}

export type ConnectionSummary = {
  lastIp?: string
  lastUserAgent?: string
  eventsInHistory?: number
  distinctIpAndClientFingerprints?: number
}

type UserConnectionInsightsProps = {
  status?: string
  lastConnection?: string | Date
  numConnections?: number
  connectionSummary?: ConnectionSummary
  connectionHistory?: ConnectionHistoryRow[]
}

const UserConnectionInsights: React.FC<UserConnectionInsightsProps> = ({
  status,
  lastConnection,
  numConnections,
  connectionSummary,
  connectionHistory,
}) => {
  const intl = useIntl()
  const [showConnectionHistory, setShowConnectionHistory] = useState(false)

  const lastIp = connectionSummary?.lastIp
  const lastUa = connectionSummary?.lastUserAgent
  const distinct = connectionSummary?.distinctIpAndClientFingerprints
  const history = Array.isArray(connectionHistory) ? connectionHistory : []

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '0.75rem',
        padding: '1rem',
        backgroundColor: '#f2f4f6',
        borderRadius: '0.75rem',
      }}
    >
      <div style={{display: 'flex', gap: '0.75rem', alignItems: 'flex-start'}}>
        <span className='material-symbols-outlined' style={{fontSize: '18px', color: '#737783', flexShrink: 0}}>
          info
        </span>
        <div style={{flex: 1, minWidth: 0}}>
          <div style={{display: 'flex', gap: '1.5rem', flexWrap: 'wrap', fontSize: '0.8125rem', color: '#434652'}}>
            {status != null && (
              <span>
                <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.STATUS'})}: </span>
                <span style={{fontWeight: 600, color: '#006876'}}>{String(status)}</span>
              </span>
            )}
            {lastConnection && (
              <span>
                <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.LAST_CONNECTION'})}: </span>
                <span style={{fontWeight: 600}}>
                  {intl.formatDate(new Date(lastConnection), {dateStyle: 'medium', timeStyle: 'short'})}
                </span>
              </span>
            )}
            {numConnections != null && (
              <span>
                <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.CONNECTIONS_COUNT'})}: </span>
                <span style={{fontWeight: 600}}>{String(numConnections)}</span>
              </span>
            )}
          </div>
          {(lastIp || lastUa || (distinct != null && distinct > 0)) && (
            <div
              style={{
                marginTop: '0.625rem',
                display: 'flex',
                flexDirection: 'column',
                gap: '0.35rem',
                fontSize: '0.8125rem',
                color: '#434652',
              }}
            >
              {lastIp ? (
                <span>
                  <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.LAST_IP'})}: </span>
                  <span style={{fontWeight: 600, fontFamily: 'ui-monospace, monospace'}}>
                    {formatDisplayIp(lastIp)}
                  </span>
                </span>
              ) : null}
              {lastUa ? (
                <span>
                  <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.LAST_CLIENT'})}: </span>
                  <span style={{fontWeight: 600}}>{summarizeUserAgent(lastUa)}</span>
                </span>
              ) : null}
              {distinct != null && distinct > 0 ? (
                <span>
                  <span style={{color: '#737783'}}>{intl.formatMessage({id: 'USERS.FORM.DISTINCT_CLIENTS'})}: </span>
                  <span style={{fontWeight: 600}}>{distinct}</span>
                  <span style={{color: '#737783', fontWeight: 400, marginLeft: '0.25rem'}}>
                    ({intl.formatMessage({id: 'USERS.FORM.DISTINCT_CLIENTS_HINT'})})
                  </span>
                </span>
              ) : null}
            </div>
          )}
          {history.length > 0 ? (
            <div style={{marginTop: '0.5rem'}}>
              <button
                type='button'
                onClick={() => setShowConnectionHistory((v) => !v)}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.35rem',
                  padding: 0,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: '0.8125rem',
                  fontWeight: 700,
                  color: '#00347f',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <span className='material-symbols-outlined' style={{fontSize: '18px'}}>
                  {showConnectionHistory ? 'expand_less' : 'expand_more'}
                </span>
                {intl.formatMessage({id: 'USERS.FORM.CONNECTION_HISTORY_TOGGLE'})}
              </button>
              {showConnectionHistory ? (
                <ul
                  style={{
                    margin: '0.5rem 0 0',
                    padding: '0 0 0 1.1rem',
                    fontSize: '0.75rem',
                    color: '#434652',
                    lineHeight: 1.45,
                    maxHeight: '11rem',
                    overflowY: 'auto',
                  }}
                >
                  {history.map((row, idx) => (
                    <li key={`${row.at}-${idx}`} style={{marginBottom: '0.35rem'}}>
                      <span style={{fontWeight: 600}}>
                        {row.at
                          ? intl.formatDate(new Date(row.at), {
                              dateStyle: 'short',
                              timeStyle: 'short',
                            })
                          : '—'}
                      </span>
                      {row.ip ? (
                        <>
                          {' · '}
                          <span style={{fontFamily: 'ui-monospace, monospace'}}>{formatDisplayIp(row.ip)}</span>
                        </>
                      ) : null}
                      {row.userAgent ? (
                        <>
                          {' · '}
                          {summarizeUserAgent(row.userAgent)}
                        </>
                      ) : null}
                    </li>
                  ))}
                </ul>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  )
}

export {UserConnectionInsights}
